import { useState, useRef, DragEvent } from 'react';
import { Folder, Upload, AlertTriangle, Clock, X } from 'lucide-react';
import { dataRepository } from '../repository/DataRepository';
import {
  validateFile,
  copyFileToVault,
  generateImageThumbnail,
  storeThumbnail,
  isImage,
  formatFileSize,
  getFileIcon
} from '../utils/fileStorage';
import type { SourceType } from '../models/types';
import './FileUploader.css';

// Determine source type from MIME type
function getSourceTypeFromMimeType(mimeType: string): SourceType {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  return 'other';
}

interface FileUploaderProps {
  onFileUploaded?: (objectId: string) => void;
  onClose?: () => void;
}

export default function FileUploader({ onFileUploaded, onClose }: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]); // Handle first file only for now
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file: File) => {
    setError(null);
    setUploadProgress(0);

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setIsUploading(true);

    try {
      // Create source object in database (using new unified 'source' type)
      const fileObject = await dataRepository.createObject(file.name, 'source');

      setUploadProgress(20);

      // Copy file to vault storage (IndexedDB)
      const { filePath, fileName, fileSize, mimeType } = await copyFileToVault(file, fileObject.id);

      setUploadProgress(50);

      // Generate thumbnail for images
      let thumbnailPath: string | undefined;
      if (isImage(mimeType)) {
        try {
          const thumbnail = await generateImageThumbnail(file);
          thumbnailPath = await storeThumbnail(fileObject.id, thumbnail);
          setUploadProgress(70);
        } catch (thumbError) {
          console.warn('Failed to generate thumbnail:', thumbError);
          // Continue even if thumbnail fails
        }
      }

      setUploadProgress(80);

      // Determine source type from MIME type
      const sourceType = getSourceTypeFromMimeType(mimeType);

      // Update source object with file metadata
      await dataRepository.updateObject(fileObject.id, {
        sourceType,
        fileName,
        filePath,
        fileSize,
        mimeType,
        thumbnailPath,
        ocrProcessed: false
      });

      setUploadProgress(100);

      // Success!
      setTimeout(() => {
        if (onFileUploaded) {
          onFileUploaded(fileObject.id);
        }
        if (onClose) {
          onClose();
        }
      }, 500);

    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      setIsUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-uploader-overlay" onClick={onClose}>
      <div className="file-uploader-modal" onClick={(e) => e.stopPropagation()}>
        <div className="file-uploader-header">
          <h2><Upload size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} /> Upload File</h2>
          <button onClick={onClose} className="close-btn"><X size={20} /></button>
        </div>

        <div className="file-uploader-content">
          {!isUploading ? (
            <>
              <div
                className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
              >
                <div className="drop-zone-icon"><Folder size={48} /></div>
                <p className="drop-zone-text">
                  Drag and drop a file here, or click to browse
                </p>
                <p className="drop-zone-hint">
                  All file types supported • Max 100MB
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />

              {error && (
                <div className="file-upload-error">
                  <span className="error-icon"><AlertTriangle size={16} /></span>
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="file-upload-progress">
              <div className="progress-icon"><Clock size={32} /></div>
              <h3>Uploading...</h3>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="progress-text">{uploadProgress}%</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
